// src/features/editor/components/course-settings/MapSection.tsx
import { useState } from 'react'
import type { CourseUpdate, CourseWithoutBlocks, Mascot } from '@/types/course'
import {
  applyMascotOp,
  lessonsToUnlockSlot,
  mascotMapSlotCount,
  toMascotInputs,
} from '../../lib/mascotSlots'
import { REVIEW_LOCK_TEXT } from '../../lib/reviewLockText'
import type { MascotOp } from '../../lib/mascotSlots'
import { InfoPanel } from './InfoPanel'
import { RoadmapPreview } from './RoadmapPreview'
import { MascotSlot } from './MascotSlot'
import { useMessages } from '@/i18n'
import { messages } from './messages'

interface Props {
  course: CourseWithoutBlocks
  save: (patch: CourseUpdate) => Promise<boolean>
  isCourseAuthor: boolean
  /** Курс на проверке: маскоты — картинки курса из заявки, запись отбивается. */
  isFrozen: boolean
}

// Места под маскотов открываются так: первое с 5 уроков, дальше по одному на каждые 3 — второе
// с 8, третье с 11 и так далее. Положение мест задаёт колонка маскотов карты (чётное — справа от
// дорожки, нечётное — слева, как в плеере). Ряда «Остальные маскоты» нет (п.10), но `mascots`
// на бэке — ПОЛНАЯ замена массива, поэтому слот обязан собирать список целиком.
type Text = (typeof messages)['ru']

const slotLabels = (m: Text, index: number) => ({
  uploadLabel: m.slotUpload(index + 1),
  replaceLabel: m.slotReplace(index + 1),
  removeLabel: m.slotRemove(index + 1),
  fileLabel: m.slotFile(index + 1),
})

/** Почему место закрыто (подсказка при наведении) или `undefined`, если открыто. */
const slotHint = (
  m: Text,
  isFrozen: boolean,
  lessons: number,
  index: number,
  filledBefore: boolean,
): string | undefined => {
  if (isFrozen) return REVIEW_LOCK_TEXT
  const need = lessonsToUnlockSlot(index)
  if (lessons < need) return m.slotOpensAt(need)
  return filledBefore ? undefined : m.slotFillPrevious
}

export const MapSection = ({ course, save, isCourseAuthor, isFrozen }: Props) => {
  const m = useMessages(messages)
  const locked = !isCourseAuthor || isFrozen
  // Черновик, а не чтение прямо из course (как в CatalogSection): mascots — полная замена
  // на бэке, а обновлённый course приходит асинхронно (updateCourse только инвалидирует тег
  // и ждёт GET всего курса). Без черновика второе действие в окне рефетча считало бы список
  // от данных ДО первого PUT — воскрешало бы уже удалённого маскота и теряло свежую загрузку.
  // Он же даёт мгновенный отклик: слот сразу показывает картинку, а удалённый — пустую зону.
  // Засев на маунте достаточен: секция живёт внутри DialogContent и вкладки, Radix
  // размонтирует её на закрытии, т.е. каждое открытие диалога заново читает актуальный course.
  const [mascots, setMascots] = useState<Mascot[]>(() => course.mascots)

  const apply = (op: MascotOp): Promise<boolean> => {
    const next = applyMascotOp(mascots, op)
    setMascots(next)
    return save({ mascots: toMascotInputs(next) })
  }

  const lessons = course.lessons?.length ?? 0
  // На карте — только открытые места (и уже занятые сверх них). Следующее закрытое
  // не рисуем плиткой «С N уроков»: на телефоне подпись не помещалась, а погашенная
  // зона выглядела сломанной кнопкой.
  const slotCount = mascotMapSlotCount(lessons, mascots.length)
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium leading-none">{m.mapMascotsTitle}</h3>
      {/* Фон в файле автора студия не убирает: сервер прозрачность сохраняет, и
          непрозрачный фон картинки так и встаёт квадратом на карту (жалоба с беты). */}
      <InfoPanel>
        <p>{m.mascotTransparentHint}</p>
      </InfoPanel>
      {/* Только пока мест нет вовсе: дальше закрытые узлы на карте и так говорят сами за себя. */}
      {slotCount === 0 ? (
        <InfoPanel>
          <p>{m.mascotUnlockFirst(lessonsToUnlockSlot(0))}</p>
        </InfoPanel>
      ) : null}

      <RoadmapPreview
        lessonsCount={lessons}
        slots={Array.from({ length: slotCount }, (_, index) => {
          // Место нельзя занять «через одно»: слот n открывается, когда заполнены все до него —
          // иначе applyMascotOp положил бы картинку в первое свободное место, а не в этот слот.
          const hint = slotHint(m, isFrozen, lessons, index, mascots.length >= index)
          const labels = slotLabels(m, index)
          return (
            <MascotSlot
              key={labels.uploadLabel}
              image={mascots[index]?.image ?? null}
              disabled={locked}
              uploadLocked={hint !== undefined}
              disabledHint={hint}
              {...labels}
              onUploaded={(image) => apply({ type: 'setSlot', slot: index, image })}
              onRemove={() => apply({ type: 'removeAt', index })}
            />
          )
        })}
      />

      {/* Один маскот — одно место: карта рисует каждого загруженного маскота, отдельного
          списка под ней нет. Промпты маскотов в студии не правятся (`SHOW_IMAGE_PROMPTS`
          выключен); если их включат, редактор промпта встанет на слот карты. `mascots` на
          бэке — полная замена массива, поэтому `apply` собирает список целиком. */}
    </div>
  )
}
